import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  DraftListingSection,
  useDraftListing,
} from '../../../contexts/DraftListingContext';
import FormAccordion from '../../common/FormAccordion';
import PropertyTypeGrid from './components/PropertyTypeGrid';
import SubPropertyTypeSection from './components/SubPropertyTypeSection';
import { usePropertyType } from './hooks/usePropertyType';
import { useMetadata } from '../../../contexts/MetadataContext';
import { FeatureFlags } from '../../../flags';
import { useFeatureFlags } from 'src/contexts/FeatureFlagsContext';
import { PropertyField } from '../../../system/field';

interface PropertyTypeProps {
  highlight: string[];
}

const PropertyType: React.FC<PropertyTypeProps> = ({ highlight }) => {
  const { show } = useDraftListing();
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);
  const { field } = useMetadata();
  const { hasFeature, getFeatureValue } = useFeatureFlags();

  const hasSubPropertyTypeFeature = hasFeature(
    FeatureFlags.CREATE_SUB_PROPERTY_TYPE_FILTER,
  );

  const allowedSubPropertyTypes = getFeatureValue(
    FeatureFlags.CREATE_SUB_PROPERTY_TYPE_FILTER,
  );

  const { value, highlightedOptions, nonHighlightedOptions, handleChange } =
    usePropertyType(highlight);

  const showSubTypesForPropertyType = useMemo(() => {
    const options = [...highlightedOptions, ...nonHighlightedOptions];
    const displayValue = options.find(
      (o) => o.LookupValue === value,
    )?.DisplayValue;
    if (displayValue) {
      return Object.keys(allowedSubPropertyTypes).includes(displayValue);
    } else {
      return false;
    }
  }, [
    allowedSubPropertyTypes,
    highlightedOptions,
    nonHighlightedOptions,
    value,
  ]);

  const metadataHasSubPropertyType = Boolean(
    field(PropertyField.PROPERTY_SUB_TYPE),
  );

  // Auto-expand if selected property type is below the fold
  useEffect(() => {
    if (!hasAutoExpanded && value && nonHighlightedOptions.length > 0) {
      const isSelectedBelowFold = nonHighlightedOptions.some(
        (option) => option.LookupValue === value,
      );
      if (isSelectedBelowFold) {
        setIsExpanded(true);
        setHasAutoExpanded(true);
      }
    }
  }, [value, nonHighlightedOptions, hasAutoExpanded]);

  if (!show(DraftListingSection.PropertyType)) {
    return null;
  }

  return (
    <FormAccordion
      title="Property Type"
      padding={0}
      subtext="We've identified the below property types based on your listing's location."
    >
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        {/* Main highlighted options */}
        <PropertyTypeGrid
          options={highlightedOptions}
          value={value}
          onChange={handleChange}
        />
        {/* View additional property types button */}
        {nonHighlightedOptions.length > 0 && (
          <Box sx={{ mt: 4, ml: -1, display: 'flex', justifyContent: 'start' }}>
            <Button
              onClick={() => setIsExpanded(!isExpanded)}
              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                color: 'secondary.main',
                textTransform: 'none',
                fontWeight: '600',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                },
                '&:active': {
                  backgroundColor: 'transparent',
                },
              }}
              disableRipple
            >
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  lineHeight: 1.5,
                }}
              >
                {`${isExpanded ? 'Hide' : 'View Additional'} Property Types`}
              </Typography>
            </Button>
          </Box>
        )}
        {/* Expanded additional options */}
        {isExpanded && nonHighlightedOptions.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <PropertyTypeGrid
              options={nonHighlightedOptions}
              value={value}
              onChange={handleChange}
            />
          </Box>
        )}
        {metadataHasSubPropertyType &&
          hasSubPropertyTypeFeature &&
          showSubTypesForPropertyType && (
            <SubPropertyTypeSection propertyType={value} />
          )}
      </Box>
    </FormAccordion>
  );
};

export default PropertyType;
